import { formatThousands, tashkentWall } from '@noor/lib';
import { resolveEntityName } from './names';
import { isLocalizedText } from './entity-bag';

/**
 * Single date format for the whole moderation surface (`21.09.2026 19:07`):
 * server stamps are naive UTC, always rendered as Tashkent wall time
 * (UTC+5) regardless of the viewer's zone. Numeric, locale-independent.
 * Missing reads as `—`, unparseable echoes back raw.
 */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = tashkentWall(value);
  return parsed.isValid() ? parsed.format('DD.MM.YYYY HH:mm') : value;
}

/** Date-only sibling (`21.09.2026`), same Tashkent wall time. */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = tashkentWall(value);
  return parsed.isValid() ? parsed.format('DD.MM.YYYY') : value;
}

/** Time-only sibling (`19:07`), same Tashkent wall time. */
export function formatTime(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = tashkentWall(value);
  return parsed.isValid() ? parsed.format('HH:mm') : value;
}

/** `25 000 сум`-style money; missing/non-numeric reads as `—`. */
export function formatMoney(value: unknown, unit = 'сум'): string {
  if (value === null || value === undefined) return '—';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${formatThousands(n)} ${unit}`;
}

/** Compact one-line rendering of any entity-bag value for diff tables. */
export function formatBagValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value.trim() === '' ? '—' : value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (isLocalizedText(value)) return resolveEntityName(value, '—');
  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    const preview = value
      .slice(0, 3)
      .map((item) => formatBagValue(item))
      .join(', ');
    return value.length > 3 ? `${preview} (+${value.length - 3})` : preview;
  }
  if (typeof value === 'object') {
    try {
      const json = JSON.stringify(value);
      return json.length > 120 ? `${json.slice(0, 117)}…` : json;
    } catch {
      return '—';
    }
  }
  return '—';
}

/** Human-readable rendering for structured fields in changed-field rows. */
export function formatChangedFieldValue(key: string, value: unknown): string {
  if (
    key !== 'address' ||
    value === null ||
    typeof value !== 'object' ||
    Array.isArray(value)
  ) {
    return formatBagValue(value);
  }

  const address = value as Record<string, unknown>;
  const line = address.line;
  if (isLocalizedText(line)) return resolveEntityName(line, '—');
  if (typeof line === 'string' && line.trim() !== '') return line;

  const latitude = typeof address.latitude === 'number' ? address.latitude : undefined;
  const longitude = typeof address.longitude === 'number' ? address.longitude : undefined;
  return latitude !== undefined && longitude !== undefined
    ? `${latitude}, ${longitude}`
    : '—';
}

/** Short human id (`93c4dd52…`) for dense cells; the full id stays in the sub-line. */
export function shortId(id: string | null | undefined): string {
  const trimmed = id?.trim() ?? '';
  if (trimmed === '') return '—';
  return trimmed.length > 8 ? `${trimmed.slice(0, 8)}…` : trimmed;
}
